<template>
  <v-stepper
    v-model="currentStep"
    class="d-flex flex-column"
    elevation="0"
    height="100%"
    data-testid="station-download-stepper"
  >
    <v-stepper-header class="height-56">
      <div class="pl-8 d-flex align-center">
        {{ datasetStore.metadata?.title }}
      </div>
      <v-stepper-item
        :complete="currentStep > 1"
        complete-icon="mdi-check"
        color="primary"
        :value="1"
      >
        {{
          t('download_form.choose.station') +
            ` (${stationStore.selectedStations.length})`
        }}
      </v-stepper-item>

      <v-stepper-item
        :complete="currentStep > 2"
        complete-icon="mdi-check"
        color="primary"
        :value="2"
      >
        {{
          t('download_form.choose.parameter') +
            ` (${parameterStore.selectedParameters.length})`
        }}
      </v-stepper-item>

      <v-stepper-item
        :value="3"
        color="primary"
      >
        {{ t('download_form.to_download') }}
      </v-stepper-item>
    </v-stepper-header>
    <v-stepper-window
      v-model="currentStep"
      class="fill-height ma-0"
    >
      <v-stepper-window-item
        class="fill-height pa-4"
        :value="1"
      >
        <keep-alive>
          <station-selection-page
            v-if="currentStep === 1"
            ref="stationPage"
          />
        </keep-alive>
      </v-stepper-window-item>
      <v-stepper-window-item
        class="fill-height pa-4"
        :value="2"
      >
        <keep-alive>
          <parameter-selection-page
            v-if="currentStep === 2"
            ref="parameterPage"
          />
        </keep-alive>
      </v-stepper-window-item>
      <v-stepper-window-item
        class="fill-height pa-4"
        :value="3"
      >
        <keep-alive>
          <station-download-page
            v-if="currentStep === 3"
            ref="downloadPage"
          />
        </keep-alive>
      </v-stepper-window-item>
    </v-stepper-window>
    <stepper-navigation
      :hide-next="hideNextButton"
      :hide-previous="hidePreviousButton"
      :allow-next="enableNextButton"
      @previous="previousStep"
      @next="nextStep"
      @abort="abort"
    />
  </v-stepper>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import StationStore from '@/stores/station/station-module';
import DatasetStore from '@/stores/dataset/dataset-module';
import ParameterStore from '@/stores/parameter/parameter-module';
import StationDownloadPage from '@/components/station/StationDownloadPage.vue';
import StepperNavigation from '@/components/common/StepperNavigation.vue';
import ParameterSelectionPage from '@/components/common/ParameterSelectionPage.vue';
import StationSelectionPage from '@/components/station/StationSelectionPage.vue';
import { useI18n } from "vue-i18n";

export default defineComponent ({
  name: 'StationDownloadStepper',
  components: {
    StepperNavigation,
    StationDownloadPage,
    ParameterSelectionPage,
    StationSelectionPage,
  },
  setup () {
    const { t } = useI18n();

    const stationPage = ref<InstanceType<typeof StationSelectionPage> | null>(null);
    const parameterPage = ref<InstanceType<typeof ParameterSelectionPage> | null>(null);
    const downloadPage = ref<InstanceType<typeof StationDownloadPage> | null>(null);

    const datasetStore = DatasetStore;
    const parameterStore = ParameterStore;
    const stationStore = StationStore;
    const currentStep = ref(1);
    const minSteps = 1;
    const maxSteps = 3;

    const parametersAreSelected = computed<boolean>(() => {
      return parameterStore.selectedParameters.length > 0;
    });

    function abort() {
      stationPage.value?.resetStationsAndFilters();
      parameterPage.value?.resetParameters();
      currentStep.value = 1;
    }

    const hidePreviousButton = computed<boolean>(() => {
      return currentStep.value <= minSteps;
    });

    const hideNextButton = computed<boolean>(() => {
      return currentStep.value >= maxSteps;
    });

    const enableNextButton = computed<boolean>(() => {
      if (currentStep.value === 1) {
        return stationStore.stationsAreSelected;
      }
      if (currentStep.value === 2) {
        return parametersAreSelected.value;
      }
      return false;
    });

    function nextStep() {
      if (currentStep.value < maxSteps) {
        currentStep.value += 1;
      }
    }

    function previousStep() {
      if (currentStep.value > minSteps) {
        currentStep.value -= 1;
      }
    }

    return {
      t,
      stationPage,
      parameterPage,
      downloadPage,
      datasetStore,
      parameterStore,
      stationStore,
      currentStep,
      abort,
      hidePreviousButton,
      hideNextButton,
      enableNextButton,
      nextStep,
      previousStep,
    }
  },
});
</script>

<style scoped>
.height-56 {
  height: 56px;
  min-height: 56px;
  max-height: 56px;
}

:deep(.v-stepper__step) {
  /*overwrite padding inside of stepper header to make sure that text is centered vertically*/
  padding: 0 24px 0 24px;
}

:deep(.v-stepper__wrapper) {
  /*scale stepper to full available height */
  height: 100%;
}

:deep(.v-stepper__wrapper),
.v-stepper__items {
  /*Needed to show the box-shadows of cards in the stepper  */
  overflow: visible;
}

:deep(.v-stepper-item--complete .v-stepper-item__avatar .v-icon) {
  color: rgb(var(--v-theme-on-primary)) !important; /* icon color */
}

:deep(.v-stepper-header) {
  background-color: rgb(var(--v-theme-secondary));
  box-shadow: none !important;
}
</style>
